import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Service } from '../types';

interface ServiceCardProps {
  service: Service;
}

const ServiceCard = ({ service }: ServiceCardProps) => {
  return (
    <div className="group flex flex-col bg-card rounded-2xl border border-border shadow-sm hover:shadow-2xl hover:shadow-primary/10 transition-all duration-300 hover:-translate-y-1 overflow-hidden h-full">
      
      {/* Top Accent Line */}
      <div className="h-1.5 w-full bg-gradient-to-r from-primary to-accent" />

      <div className="p-8 flex-1 flex flex-col">
        
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
            <Icon name={service.icon} size={28} className="text-primary group-hover:text-white transition-colors" />
          </div>
          <span className="text-[10px] font-bold text-primary bg-primary/5 border border-primary/20 px-3 py-1 rounded-full uppercase tracking-wider">
            {service.category}
          </span>
        </div>
        
        <h3 className="text-2xl font-bold text-text-primary mb-3">
          {service.title}
        </h3>
        
        <p className="text-text-secondary mb-8 leading-relaxed text-sm flex-1">
          {service.description}
        </p>
        
        {/* Features List */}
        <div className="mb-8 p-5 bg-surface rounded-xl border border-border/50">
          <h4 className="text-xs font-bold text-text-primary uppercase tracking-wide mb-3 flex items-center gap-2">
            <Icon name="Zap" size={12} className="text-accent" /> What's Included:
          </h4>
          <ul className="space-y-2.5">
            {service.features.slice(0, 4).map((feature, index) => (
              <li key={index} className="flex items-start space-x-2.5 text-sm text-text-secondary">
                <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Tech Stack */}
        <div className="mb-8">
          <h4 className="text-[10px] font-bold text-text-secondary uppercase tracking-wide mb-3">
            Tech Stack
          </h4>
          <div className="flex flex-wrap gap-3">
            {service.tools.map((tool, index) => (
              <div 
                key={index} 
                className="flex items-center space-x-2 bg-background border border-border px-3 py-1.5 rounded-lg group/tool hover:border-primary/50 transition-colors"
              >
                <div className="w-4 h-4 filter grayscale group-hover/tool:grayscale-0 transition-all">
                  <Image src={tool.logo} alt={tool.alt} className="w-full h-full object-contain" />
                </div>
                <span className="text-xs font-medium text-text-primary">{tool.name}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Footer: Pricing & Action */}
        <div className="pt-6 border-t border-border mt-auto">
          <div className="flex items-end justify-between mb-5">
            <div>
              <span className="text-xs text-text-secondary block mb-1">Starting from</span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-text-primary">{service.pricing.starter}</span>
                {service.pricing.starter !== 'Custom' && (
                  <span className="text-xs text-text-secondary font-medium">/month</span>
                )}
              </div>
            </div>
          </div>
          
          <Link to={service.route}>
            <Button 
              variant="outline" 
              fullWidth 
              iconName="ArrowRight" 
              iconPosition="right"
              className="group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all duration-300"
            >
              View Full Details
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;